import PropTypes from "prop-types"
import React from "react"

import Button from "src/components/Button"
import PreviewSheet from "src/components/PreviewSheet"

import { inchesToFeet } from "src/utils/UnitConversion"

const STATUS_CONFIG = {
  active: { icon: "icon-check", color: "text-teal-600", label: "Active" },
  expired: {
    icon: "icon-exclamation-circle",
    color: "text-red-600",
    label: "Expired",
  },
  missing: {
    icon: "icon-question-circle-btl",
    color: "text-gray-500",
    label: "Missing",
  },
}

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status]
  if (!config) return null

  return (
    <div className="flex items-center gap-1 text-sm">
      <i className={`icon ${config.icon} ${config.color}`} />
      <span className={config.color}>{config.label}</span>
    </div>
  )
}

StatusBadge.propTypes = {
  status: PropTypes.oneOf(["active", "expired", "missing"]).isRequired,
}

const BoatPreviewContent = ({ boat }) => {
  const lengthFeet = boat.lengthOverall
    ? inchesToFeet(boat.lengthOverall)
    : null
  const beamFeet = boat.beam ? inchesToFeet(boat.beam) : null
  const heightFeet = boat.height ? inchesToFeet(boat.height) : null
  const drawFeet = boat.draw ? inchesToFeet(boat.draw) : null

  return (
    <div className="flex gap-4">
      <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-blue-50">
        {boat.photoUrl ? (
          <img
            src={boat.photoUrl}
            alt={boat.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-blue-400">
            <i className="icon icon-md-directions-boat text-2xl" />
          </div>
        )}
      </div>
      <div className="flex flex-col justify-center">
        <div className="font-semibold text-gray-900">{boat.name}</div>
        {(boat.year || boat.make || boat.model) && (
          <div className="text-sm text-gray-600">
            {[boat.year, boat.make, boat.model].filter(Boolean).join(" ")}
          </div>
        )}
        <div className="mt-1 text-sm text-gray-500">
          {[
            lengthFeet && `${lengthFeet}' LOA`,
            beamFeet && `${beamFeet}' Beam`,
            heightFeet && `${heightFeet}' Height`,
            drawFeet && `${drawFeet}' Draft`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
        {boat.hullIdentification && (
          <div className="mt-1 text-sm text-gray-500">
            HIN: {boat.hullIdentification}
          </div>
        )}
        {(boat.registrationNumber ||
          boat.registrationStatus ||
          boat.insuranceStatus) && (
          <div className="mt-2 flex flex-col gap-1 border-t pt-2">
            {boat.registrationStatus && (
              <div className="flex gap-2 text-sm text-gray-600">
                <span>
                  Registration
                  {boat.registrationNumber && ` # ${boat.registrationNumber}`}
                </span>
                <StatusBadge status={boat.registrationStatus} />
              </div>
            )}
            {boat.insuranceStatus && (
              <div className="flex gap-2 text-sm text-gray-600">
                <span>Insurance</span>
                <StatusBadge status={boat.insuranceStatus} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

BoatPreviewContent.propTypes = {
  boat: PropTypes.shape({
    name: PropTypes.string.isRequired,
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
    lengthOverall: PropTypes.number,
    beam: PropTypes.number,
    height: PropTypes.number,
    draw: PropTypes.number,
    photoUrl: PropTypes.string,
    registrationNumber: PropTypes.string,
    registrationStatus: PropTypes.oneOf(["active", "expired", "missing"]),
    hullIdentification: PropTypes.string,
    insuranceStatus: PropTypes.oneOf(["active", "expired", "missing"]),
  }).isRequired,
}

const BoatPreviewLink = ({ boat }) => {
  return (
    <PreviewSheet
      href={boat.url}
      cta={
        <Button variant="primary" fullWidth>
          View Full Details
        </Button>
      }
    >
      <PreviewSheet.Trigger>
        <span className="font-semibold text-blue-600">{boat.name}</span>
      </PreviewSheet.Trigger>
      <PreviewSheet.Content>
        <BoatPreviewContent boat={boat} />
      </PreviewSheet.Content>
    </PreviewSheet>
  )
}

BoatPreviewLink.propTypes = {
  boat: PropTypes.shape({
    name: PropTypes.string.isRequired,
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
    lengthOverall: PropTypes.number,
    beam: PropTypes.number,
    height: PropTypes.number,
    draw: PropTypes.number,
    photoUrl: PropTypes.string,
    registrationNumber: PropTypes.string,
    registrationStatus: PropTypes.oneOf(["active", "expired", "missing"]),
    insuranceStatus: PropTypes.oneOf(["active", "expired", "missing"]),
    hullIdentification: PropTypes.string,
    url: PropTypes.string.isRequired,
  }).isRequired,
}

export default BoatPreviewLink
